<script setup lang="ts">
import { ALButton } from '@af-lib/vue-ui';
import { computed, inject } from 'vue';

import { companySpaces, getSpacesRole, redirectToSpaces, roleSpaces } from '@/helpers/spaces';
import { useAuthStore } from '@/stores/auth';
import type { Translate } from '@/types/i18n/i18n.types';

const t = inject('t') as Translate;

const authStore = useAuthStore();

const onInvalidSpaces = computed(() => getSpacesRole() === 'invalidSpaces');

const ROLES = {
  recruiter: {
    color: 'text-roleRecruiter',
    key: t('general.role.recruiter')
  },
  manager: {
    color: 'text-roleManager',
    key: t('general.role.manager'),
    tooltip: t('navigation.go_to.space_tooltip')
  },
  admin: {
    color: 'text-roleAdmin',
    key: t('general.role.admin'),
    tooltip: t('navigation.go_to.space_tooltip')
  }
};

function getSpacesLabel(): string | undefined {
  switch (roleSpaces.value) {
    case 'admin':
    case 'recruiter':
      return authStore.company?.label;
    case 'manager':
      return authStore.managers?.find(manager => manager?.company.uuid === companySpaces.value)?.company.label;
    default:
      return undefined;
  }
}
</script>

<template>
  <v-divider v-if="roleSpaces" class="mb-4"></v-divider>
  <div v-if="roleSpaces" class="px-4 gap-12 d-grid">
    <p class="text-body-2 font-weight-bold text-ellipsis" data-cy="admin-spaces-menu-space">
      {{ getSpacesLabel() }}
    </p>
    <div class="d-flex align-end justify-space-between gap-12 mb-4">
      <p
        :class="`text-subtitle-3 font-weight-bold d-flex align-center gap-4 ${ROLES[roleSpaces].color}`"
        data-cy="admin-spaces-menu-role"
      >
        <v-icon v-if="onInvalidSpaces" icon="circle-exclamation" class="text-icon-4"></v-icon>
        {{ ROLES[roleSpaces].key }}
        <v-tooltip
          v-if="onInvalidSpaces && ROLES[roleSpaces]?.tooltip"
          id="invalid-spaces"
          activator="parent"
          :text="ROLES[roleSpaces].tooltip"
          :aria-label="ROLES[roleSpaces].tooltip"
          location="top"
        />
      </p>
      <ALButton type="tertiary" class="h-auto" data-cy="admin-spaces-menu-btn" @click="redirectToSpaces">
        {{ t('navigation.go_to.space') }}
      </ALButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.text-ellipsis {
  text-overflow: ellipsis;
  max-width: 235px;
  white-space: nowrap;
  overflow: hidden;
}
</style>
